import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Phone, MessageCircle, Clock, Users } from 'lucide-react';

function Contact() {
  const { t } = useLanguage();
  const phoneNumbers = [
    { number: '9848756900', label: 'Primary Contact' },
    { number: '9843101478', label: 'Secondary Contact' }
  ];

  const handleCall = (number) => {
    window.open(`tel:${number}`, '_self');
  };

  const handleWhatsApp = (number) => {
    window.open(`https://wa.me/${number}`, '_blank');
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-4 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full">
            <Users className="h-12 w-12 text-blue-600" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('Contact Customer Support')}</h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          {t('Need help with your order or have questions? Our friendly customer support team is here to assist you.')}
        </p>
      </div>

      {/* Contact Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {phoneNumbers.map((contact, index) => (
          <div key={index} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Phone className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">{contact.label}</h3>
                  <p className="text-sm text-gray-600">{t('Customer Support Line')}</p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="text-2xl font-bold text-gray-900 mb-2">
                  {contact.number}
                </div>
                <p className="text-sm text-gray-500">{t('Available for calls and WhatsApp')}</p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => handleCall(contact.number)}
                  className="flex-1 bg-green-600 text-white py-3 px-4 rounded-xl hover:bg-green-700 transition-colors flex items-center justify-center space-x-2 font-medium"
                >
                  <Phone className="h-5 w-5" />
                  <span>{t('Call Now')}</span>
                </button>
                
                <button
                  onClick={() => handleWhatsApp(contact.number)}
                  className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 font-medium"
                >
                  <MessageCircle className="h-5 w-5" />
                  <span>{t('WhatsApp')}</span>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Additional Information */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">{t('Support Information')}</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="p-3 bg-green-100 rounded-full w-fit mx-auto mb-4">
              <Clock className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{t('Business Hours')}</h3>
            <p className="text-gray-600 text-sm">
              {t('Monday - Sunday')}<br />
              {t('9:00 AM - 10:00 PM')}
            </p>
          </div>
          
          <div className="text-center">
            <div className="p-3 bg-purple-100 rounded-full w-fit mx-auto mb-4">
              <MessageCircle className="h-6 w-6 text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{t('Response Time')}</h3>
            <p className="text-gray-600 text-sm">
              {t('Calls: Immediate')}<br />
              {t('WhatsApp: Within 5 minutes')}
            </p>
          </div>
          
          <div className="text-center">
            <div className="p-3 bg-orange-100 rounded-full w-fit mx-auto mb-4">
              <Users className="h-6 w-6 text-orange-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">{t('Support Team')}</h3>
            <p className="text-gray-600 text-sm">
              {t('Experienced staff')}<br />
              {t('Multi-language support')}
            </p>
          </div>
        </div>
      </div>



      {/* Emergency Contact */}
      <div className="bg-red-50 border border-red-200 rounded-2xl p-6 max-w-4xl mx-auto">
        <div className="flex items-center space-x-3 mb-4">
          <div className="p-2 bg-red-100 rounded-full">
            <Phone className="h-5 w-5 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-red-900">{t('Emergency Contact')}</h3>
        </div>
        <p className="text-red-800 text-sm mb-3">
          {t('For urgent issues or complaints, please call our primary contact number immediately.')}
        </p>
        <button
          onClick={() => handleCall(phoneNumbers[0].number)}
          className="bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
        >
          Call Emergency Line: {phoneNumbers[0].number}
        </button>
      </div>
    </div>
  );
}

export default Contact;